import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { AiOutlineClose, AiOutlineCloudUpload } from 'react-icons/ai';

import { destratarData } from '../../utils/tratarData';
import type { ICampos } from '.';

interface IFormularioRegistroProps {
  aberto: boolean;
  titulo: string;
  campos: ICampos[];
  registro?: { [key: string]: any };
  valorSelect: string;
  opcoesSelect: { value: number; label: string }[];
  imageInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onChange: (campo: string, valor: any) => void;
  onSelect: (valor: number) => void;
  onSalvar: () => void;
  onCancelar: () => void;
}

const classeCampo = 'w-full rounded-xl border border-zinc-300 bg-white px-4 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10';

// A API devolve datas em dd/MM/yyyy; depois que o usuário altera, o valor já está em yyyy-MM-dd (formato do input)
function valorInputData(valor?: string) {
  if (valor && /^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;
  return destratarData(valor || '');
}

function valorInput(valor: any) {
  if (valor === 0) return '0';
  return valor ?? '';
}

export default function FormularioRegistro({
  aberto,
  titulo,
  campos,
  registro,
  valorSelect,
  opcoesSelect,
  imageInputRef,
  onChange,
  onSelect,
  onSalvar,
  onCancelar,
}: IFormularioRegistroProps) {
  const [nomeArquivo, setNomeArquivo] = useState('');

  useEffect(() => {
    if (!aberto) setNomeArquivo('');
  }, [aberto]);

  function renderInput(campo: ICampos) {
    const id = `campo-${campo.campo}`;
    const valor = registro?.[campo.campo];

    switch (campo.type) {
      case 'checkbox':
        return (
          <button
            id={id}
            type='button'
            role='switch'
            aria-checked={!!valor}
            onClick={() => onChange(campo.campo, !valor)}
            className='flex h-12 w-full items-center justify-between rounded-xl border border-zinc-300 bg-white px-4 text-left text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            <span>{valor ? 'Sim' : 'Não'}</span>
            <span className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${valor ? 'bg-zinc-900' : 'bg-zinc-200'}`}>
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform motion-reduce:transition-none ${valor ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </span>
          </button>
        );
      case 'file':
        return (
          <div className='space-y-3'>
            {(typeof valor === 'string' && valor.includes('https://')) && (
              <img src={valor} alt='Imagem atual' className='aspect-video w-full rounded-xl object-cover' />
            )}
            <label
              htmlFor={id}
              className='flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 hover:border-zinc-900 hover:bg-zinc-100 focus-within:ring-2 focus-within:ring-zinc-900'
            >
              <AiOutlineCloudUpload className='h-7 w-7 text-zinc-400' aria-hidden='true' />
              <span className='font-medium text-zinc-900'>{nomeArquivo || 'Selecionar imagem'}</span>
              <span>PNG ou JPEG</span>
              <input
                id={id}
                ref={imageInputRef}
                type='file'
                accept='image/png, image/jpeg'
                onChange={(e) => setNomeArquivo(e.target.files?.[0]?.name || '')}
                className='sr-only'
              />
            </label>
          </div>
        );
      case 'textarea':
        return (
          <textarea
            id={id}
            rows={4}
            value={valorInput(valor)}
            onChange={(e) => onChange(campo.campo, e.target.value.replace(',', '.'))}
            className={`${classeCampo} resize-y py-3`}
          />
        );
      case 'select':
        return (
          <select
            id={id}
            value={valorSelect}
            onChange={(e) => onSelect(+e.target.value)}
            className={`${classeCampo} h-12`}
          >
            <option value='' disabled>Selecione</option>
            {opcoesSelect.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            id={id}
            type={campo.type || 'text'}
            inputMode={campo.type === 'number' ? 'decimal' : undefined}
            value={campo.type === 'date' ? valorInputData(valor) : valorInput(valor)}
            onChange={(e) => onChange(campo.campo, e.target.value.replace(',', '.'))}
            className={`${classeCampo} h-12`}
          />
        );
    }
  }

  return (
    <ReactModal
      isOpen={aberto}
      appElement={document.getElementById('root') as HTMLElement}
      contentLabel={titulo}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onCancelar}
      overlayClassName='fixed inset-0 z-[99] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in motion-reduce:animate-none'
      className='relative flex w-full max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-card outline-none animate-slide-up motion-reduce:animate-none md:max-w-lg md:rounded-2xl'
    >
      <form
        className='flex min-h-0 flex-1 flex-col'
        onSubmit={(e) => {
          e.preventDefault();
          onSalvar();
        }}
      >
        <div className='flex items-center justify-between border-b border-zinc-100 px-5 py-4'>
          <h2 className='text-lg font-semibold text-zinc-900'>{titulo}</h2>
          <button
            type='button'
            onClick={onCancelar}
            aria-label='Fechar'
            className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            <AiOutlineClose className='h-5 w-5' aria-hidden='true' />
          </button>
        </div>

        <div className='grid min-h-0 gap-4 overflow-y-auto px-5 py-5 sm:grid-cols-2'>
          {campos.map(campo => (
            <div key={campo.campo} className={campo.type === 'number' ? '' : 'sm:col-span-2'}>
              <label htmlFor={`campo-${campo.campo}`} className='mb-1 block text-sm font-medium text-zinc-700'>{campo.label}</label>
              {renderInput(campo)}
            </div>
          ))}
        </div>

        <div className='flex gap-3 border-t border-zinc-100 px-5 pt-4 pb-[max(16px,env(safe-area-inset-bottom))]'>
          <button
            type='button'
            onClick={onCancelar}
            className='flex h-12 flex-1 items-center justify-center rounded-xl border border-zinc-200 bg-white px-5 font-semibold text-zinc-900 transition-colors hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            Cancelar
          </button>
          <button
            type='submit'
            className='flex h-12 flex-1 items-center justify-center rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2'
          >
            Salvar
          </button>
        </div>
      </form>
    </ReactModal>
  );
}
